import { useEffect, useMemo, useState } from 'react';
import { MePrFoService, type ReshareInfoLike } from '@/services/meprfo';
import { FormLogic } from '@/modules/form';
import { TemplateLogic, type ContextVars } from '@/modules/template';
import { ReshareStoryLogic } from '@/modules/share/reshare_story_logic';
import type { Topic } from '@/modules/topic/types';
import newsDefaultShareFormYaml from './default_share_form.yaml?raw';

export interface NewsReshareStory {
  /** The leg payload, or null while it loads / when there is no news leg. */
  payload: ReshareInfoLike | null;
  /**
   * Whether the leg probe has finished, loaded or failed — or there is no leg
   * to probe. Until then the sheet does not know whether it tells a story, so
   * it cannot pick its content or its height.
   */
  settled: boolean;
  /**
   * The queried leg's reshare story in its sender's voice ("I reshared this from
   * my friend Fred. He's 2 degrees separated from Sally, the author."), as
   * the topic's reshare story settings tell it. The starting sender's own note
   * travels separately in `payload.startingAllowedDetail`. Empty when this leg
   * is the starting leg.
   */
  reshareStory: string;
  /**
   * The same goal sentence the SMS carried ("At last count 2 people saw
   * message, but he's trying to reach 3 in record time."), re-rendered from the
   * payload's live numbers and the originator's pronouns against the topic's
   * goal-slot prose. Empty until the prose resolves, so the copy never swaps
   * mid-read, and empty for a form that carries no goal slot.
   */
  goalSentence: string;
  /** The run passed its impression goal — a milestone, not a stop (#447). */
  goalReached: boolean;
  /** The topic's sharing limit closed this run to further reshares. */
  limitReached: boolean;
  /** A readable, still-open run: the reshare CTA has somewhere to go. */
  canKeepRolling: boolean;
}

/**
 * The traveling story behind a news `/b/` link: the allowed detail, who started
 * it, how far it has come, and whether the run is still open to reshares.
 *
 * `legId` is the leg the visitor arrived on; with none (a news topic opened
 * directly) everything stays empty and the welcome falls back to its standard
 * share intro.
 */
export function useNewsReshareStory(
  legId: string | undefined,
  topic: Topic | null | undefined
): NewsReshareStory {
  const [payload, setPayload] = useState<ReshareInfoLike | null>(null);
  // The leg whose probe has finished, so a new leg reads as unsettled at once.
  const [settledLegId, setSettledLegId] = useState<string | null>(null);

  useEffect(() => {
    if (!legId) {
      setPayload(null);
      return;
    }
    let cancelled = false;
    MePrFoService.getReshareInfoForLeg(legId)
      .then(news => {
        if (!cancelled) setPayload(news);
      })
      .catch(() => {
        // Best-effort: a broken probe just leaves the standard welcome copy.
      })
      .finally(() => {
        if (!cancelled) setSettledLegId(legId);
      });
    return () => {
      cancelled = true;
    };
  }, [legId]);

  // The race sentence lives in the goal slot of the form the SMS composed from:
  // the manager's custom form when it parses, else the type default — the same
  // source the share wizard composes from, so this sheet never contradicts the
  // message. A custom form that parses speaks for itself, even one whose author
  // removed the goal slot, in which case the sentence is genuinely gone.
  const goalProseTemplate = useMemo(() => {
    const custom = topic?.customTemplates;
    return custom && FormLogic.isSharingConfig(custom)
      ? FormLogic.goalProse(custom)
      : FormLogic.goalProse(newsDefaultShareFormYaml);
  }, [topic?.customTemplates]);

  // The context is assembled from the payload rather than built from a Topic:
  // the goal it names is the leg payload's server-clamped one, and a goal slot
  // only ever references these primitives. Every other primitive
  // resolves empty, exactly as it would in a full context.
  const goalSentence = useMemo(() => {
    if (!payload || payload.impressionGoal == null || !goalProseTemplate) {
      return '';
    }
    const pronouns = ReshareStoryLogic.pronounForms(
      payload.startingSenderPronouns
    );
    return TemplateLogic.renderText(goalProseTemplate, {
      _impressionGoal: String(payload.impressionGoal),
      _runImpressionCount: String(payload.runImpressionCount),
      _runImpressionPeople: ReshareStoryLogic.peoplePhrase(
        payload.runImpressionCount
      ),
      _preferredPronouns: payload.startingSenderPronouns?.trim() ?? '',
      _subjectPronoun: pronouns.subject,
      _subjectPronounIs: pronouns.subjectIs,
      _possessivePronoun: pronouns.possessive,
    } as unknown as ContextVars);
  }, [payload, goalProseTemplate]);

  const goalReached =
    !!payload &&
    ReshareStoryLogic.goalReached(
      payload.runImpressionCount,
      payload.impressionGoal
    );
  const limitReached = !!payload && payload.topicLimitReached;

  return {
    payload,
    settled: !legId || settledLegId === legId,
    reshareStory: payload?.story ?? '',
    goalSentence,
    goalReached,
    limitReached,
    canKeepRolling: !!payload && !payload.gated && !limitReached,
  };
}
